import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Users, Package, Plus, Trash2, Edit2, UserPlus, X, Shield, Briefcase, User, Eye, FolderPlus, UserCheck, UsersRound } from "lucide-react";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = {
  admin: "Admin",
  product_manager: "Product Owner",
  team_member_business: "Team Member (Business)",
  team_member_it: "Team Member (IT)"
};

const ROLE_ICONS = {
  admin: Shield,
  product_manager: Briefcase,
  team_member_business: User,
  team_member_it: User
};

export function AdminPanel() {
  const { user, emulateUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showUserProductsModal, setShowUserProductsModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedProductForAssign, setSelectedProductForAssign] = useState(null);
  const [selectedUserForProducts, setSelectedUserForProducts] = useState(null);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [userAssignedProducts, setUserAssignedProducts] = useState([]);
  const [emulating, setEmulating] = useState(false);
  const [globalStakeholders, setGlobalStakeholders] = useState([]);
  const [teams, setTeams] = useState([]);
  const [newTeamName, setNewTeamName] = useState("");
  const [showStakeholderModal, setShowStakeholderModal] = useState(false);
  const [editingStakeholder, setEditingStakeholder] = useState(null);
  const [stakeholderForm, setStakeholderForm] = useState({ name: "", position: "", type: "business", email: "" });
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Form states
  const [userForm, setUserForm] = useState({ username: "", password: "", name: "", email: "", role: "team_member_business" });
  const [productForm, setProductForm] = useState({ name: "", description: "" });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, productsRes, stakeholdersRes, teamsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/users`),
        axios.get(`${API_URL}/api/admin/products`),
        axios.get(`${API_URL}/api/admin/stakeholders`),
        axios.get(`${API_URL}/api/teams`),
      ]);
      setUsers(usersRes.data);
      setProducts(productsRes.data);
      setGlobalStakeholders(stakeholdersRes.data);
      setTeams(teamsRes.data);
    } catch (error) {
      console.error("Error fetching admin data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await axios.patch(`${API_URL}/api/admin/users/${editingUser.id}`, userForm);
      } else {
        await axios.post(`${API_URL}/api/admin/users`, userForm);
      }
      setShowUserModal(false);
      setEditingUser(null);
      setUserForm({ username: "", password: "", name: "", email: "", role: "team_member_business" });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || "Error saving user");
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    try {
      if (editingProduct) {
        await axios.patch(`${API_URL}/api/admin/products/${editingProduct.id}`, productForm);
      } else {
        await axios.post(`${API_URL}/api/admin/products`, productForm);
      }
      setShowProductModal(false);
      setEditingProduct(null);
      setProductForm({ name: "", description: "" });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || "Error saving product");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      await axios.delete(`${API_URL}/api/admin/users/${userId}`);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || "Error deleting user");
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product and all its data?")) return;
    try {
      await axios.delete(`${API_URL}/api/admin/products/${productId}`);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || "Error deleting product");
    }
  };

  const openAssignModal = async (product) => {
    setSelectedProductForAssign(product);
    try {
      const res = await axios.get(`${API_URL}/api/admin/products/${product.id}/users`);
      setAssignedUsers(res.data);
    } catch (error) {
      console.error("Error fetching assigned users:", error);
    }
    setShowAssignModal(true);
  };

  const handleAssignUser = async (userId) => {
    try {
      await axios.post(`${API_URL}/api/admin/products/${selectedProductForAssign.id}/users`, { user_id: userId });
      const res = await axios.get(`${API_URL}/api/admin/products/${selectedProductForAssign.id}/users`);
      setAssignedUsers(res.data);
    } catch (error) {
      alert(error.response?.data?.detail || "Error assigning user");
    }
  };

  const handleRemoveUser = async (userId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/products/${selectedProductForAssign.id}/users/${userId}`);
      const res = await axios.get(`${API_URL}/api/admin/products/${selectedProductForAssign.id}/users`);
      setAssignedUsers(res.data);
    } catch (error) {
      alert(error.response?.data?.detail || "Error removing user");
    }
  };

  // Global Stakeholder CRUD
  const handleCreateStakeholder = async (e) => {
    e.preventDefault();
    try {
      if (editingStakeholder) {
        await axios.patch(`${API_URL}/api/admin/stakeholders/${editingStakeholder.id}`, stakeholderForm);
      } else {
        await axios.post(`${API_URL}/api/admin/stakeholders`, stakeholderForm);
      }
      setShowStakeholderModal(false);
      setEditingStakeholder(null);
      setStakeholderForm({ name: "", position: "", type: "business", email: "" });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || "Error saving stakeholder");
    }
  };

  const handleDeleteStakeholder = async (id) => {
    if (!window.confirm("Delete this stakeholder from the directory?")) return;
    try {
      await axios.delete(`${API_URL}/api/admin/stakeholders/${id}`);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || "Error deleting stakeholder");
    }
  };

  const openEditStakeholder = (s) => {
    setEditingStakeholder(s);
    setStakeholderForm({ name: s.name, position: s.position || "", type: s.type || "business", email: s.email || "" });
    setShowStakeholderModal(true);
  };

  const openEditUser = (u) => {
    setEditingUser(u);
    setUserForm({ username: u.username, password: "", name: u.name, email: u.email || "", role: u.role });
    setShowUserModal(true);
  };

  const openEditProduct = (p) => {
    setEditingProduct(p);
    setProductForm({ name: p.name, description: p.description || "" });
    setShowProductModal(true);
  };

  const handleEmulateUser = async (userId) => {
    if (!window.confirm("You will be viewing the app as this user. Continue?")) return;
    setEmulating(true);
    try {
      await emulateUser(userId);
      // The page will reload/redirect automatically via AuthContext
    } catch (error) {
      alert(error.response?.data?.detail || "Error emulating user");
      setEmulating(false);
    }
  };

  // Open modal to manage which products a user is assigned to
  const openUserProductsModal = async (u) => {
    setSelectedUserForProducts(u);
    try {
      const res = await axios.get(`${API_URL}/api/admin/users/${u.id}/products`);
      setUserAssignedProducts(res.data.map(p => p.id));
    } catch (error) {
      console.error("Error fetching user products:", error);
      setUserAssignedProducts([]);
    }
    setShowUserProductsModal(true);
  };

  const toggleUserProduct = async (productId) => {
    const isAssigned = userAssignedProducts.includes(productId);
    try {
      if (isAssigned) {
        await axios.delete(`${API_URL}/api/admin/products/${productId}/users/${selectedUserForProducts.id}`);
        setUserAssignedProducts(prev => prev.filter(id => id !== productId));
      } else {
        await axios.post(`${API_URL}/api/admin/products/${productId}/users`, { user_id: selectedUserForProducts.id });
        setUserAssignedProducts(prev => [...prev, productId]);
      }
    } catch (error) {
      alert(error.response?.data?.detail || "Error updating assignment");
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <>
    <div className="space-y-8" data-testid="admin-panel">
      <div>
        <p className="text-xs font-semibold tracking-widest text-[#00D4C8] uppercase mb-2">Administration</p>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 font-['Outfit']">Admin Panel</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">Manage users, products, and access permissions</p>
      </div>

      <Tabs defaultValue="users" className="w-full">
        <TabsList className="mb-6 bg-slate-100 dark:bg-slate-800/60 border dark:border-slate-700">
          <TabsTrigger value="users" className="flex items-center gap-2 data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 dark:text-slate-300 dark:data-[state=active]:text-white" data-testid="users-tab">
            <Users className="w-4 h-4" /> Users ({users.length})
          </TabsTrigger>
          <TabsTrigger value="products" className="flex items-center gap-2 data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 dark:text-slate-300 dark:data-[state=active]:text-white" data-testid="products-tab">
            <Package className="w-4 h-4" /> Products ({products.length})
          </TabsTrigger>
          <TabsTrigger value="stakeholders" className="flex items-center gap-2 data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 dark:text-slate-300 dark:data-[state=active]:text-white" data-testid="stakeholders-tab">
            <UserCheck className="w-4 h-4" /> Stakeholders ({globalStakeholders.length})
          </TabsTrigger>
          <TabsTrigger value="teams" className="flex items-center gap-2 data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 dark:text-slate-300 dark:data-[state=active]:text-white" data-testid="teams-tab">
            <UsersRound className="w-4 h-4" /> Teams ({teams.length})
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">User Management</h2>
            <Button onClick={() => { setEditingUser(null); setUserForm({ username: "", password: "", name: "", email: "", role: "team_member_business" }); setShowUserModal(true); }} data-testid="add-user-btn">
              <Plus className="w-4 h-4 mr-2" /> Add User
            </Button>
          </div>
          <div className="grid gap-4">
            {users.map((u) => {
              const RoleIcon = ROLE_ICONS[u.role] || User;
              return (
                <Card key={u.id} className="card-modern" data-testid={`user-card-${u.id}`}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${u.role === 'admin' ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400' : u.role === 'product_manager' ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400' : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'}`}>
                        <RoleIcon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-800 dark:text-slate-100">{u.name}</p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">@{u.username} • {ROLE_LABELS[u.role]}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {u.id !== user.id && (
                        <Button variant="ghost" size="sm" onClick={() => handleEmulateUser(u.id)} disabled={emulating} title="View as this user" data-testid={`emulate-user-${u.id}`} className="dark:text-slate-300 dark:hover:bg-slate-700">
                          <Eye className="w-4 h-4" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => openUserProductsModal(u)} title="Assign to products" data-testid={`assign-products-${u.id}`} className="dark:text-slate-300 dark:hover:bg-slate-700">
                        <FolderPlus className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEditUser(u)} data-testid={`edit-user-${u.id}`} className="dark:text-slate-300 dark:hover:bg-slate-700">
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      {u.id !== user.id && (
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/30" onClick={() => setDeleteTarget({ fn: () => handleDeleteUser(u.id), label: u.name || u.username })} data-testid={`delete-user-${u.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Products Tab */}
        <TabsContent value="products">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Product Management</h2>
            <Button onClick={() => { setEditingProduct(null); setProductForm({ name: "", description: "" }); setShowProductModal(true); }} data-testid="add-product-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Product
            </Button>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {products.map((p) => (
              <Card key={p.id} className="card-modern" data-testid={`product-card-${p.id}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2 dark:text-slate-100">
                    <Package className="w-5 h-5 text-[#00D4C8]" />
                    {p.name}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-4 line-clamp-2">{p.description || "No description"}</p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => openAssignModal(p)} data-testid={`assign-users-${p.id}`} className="dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700">
                      <UserPlus className="w-4 h-4 mr-1" /> Assign Users
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openEditProduct(p)} data-testid={`edit-product-${p.id}`} className="dark:text-slate-300 dark:hover:bg-slate-700">
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/30" onClick={() => setDeleteTarget({ fn: () => handleDeleteProduct(p.id), label: p.name })} data-testid={`delete-product-${p.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {products.length === 0 && (
              <div className="col-span-full text-center py-12 text-slate-500 dark:text-slate-400">
                No products yet. Click "Add Product" to create one.
              </div>
            )}
          </div>
        </TabsContent>

        {/* Stakeholders Tab */}
        <TabsContent value="stakeholders">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Stakeholder Directory</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Global list of stakeholders. Import them into any product.</p>
            </div>
            <Button onClick={() => { setEditingStakeholder(null); setStakeholderForm({ name: "", position: "", type: "business", email: "" }); setShowStakeholderModal(true); }} data-testid="add-stakeholder-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Stakeholder
            </Button>
          </div>

          {/* Business Stakeholders */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-3 flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-blue-500" /> Business Stakeholders ({globalStakeholders.filter(s => s.type !== "it").length})
            </h3>
            <div className="grid gap-3">
              {globalStakeholders.filter(s => s.type !== "it").map(s => (
                <Card key={s.id} className="card-modern" data-testid={`global-stakeholder-${s.id}`}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-50 dark:bg-blue-900/40 flex items-center justify-center text-blue-600 dark:text-blue-400 font-bold text-sm">
                        {s.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-slate-800 dark:text-slate-100">{s.name}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {s.position || "No position"}{s.email ? ` · ${s.email}` : ""}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEditStakeholder(s)} data-testid={`edit-stakeholder-${s.id}`} className="dark:text-slate-300 dark:hover:bg-slate-700">
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/30" onClick={() => setDeleteTarget({ fn: () => handleDeleteStakeholder(s.id), label: s.name })} data-testid={`delete-stakeholder-${s.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {globalStakeholders.filter(s => s.type !== "it").length === 0 && (
                <p className="text-sm text-slate-400 py-4 text-center">No business stakeholders yet</p>
              )}
            </div>
          </div>

          {/* IT Stakeholders */}
          <div>
            <h3 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-3 flex items-center gap-2">
              <User className="w-4 h-4 text-purple-500" /> IT Stakeholders ({globalStakeholders.filter(s => s.type === "it").length})
            </h3>
            <div className="grid gap-3">
              {globalStakeholders.filter(s => s.type === "it").map(s => (
                <Card key={s.id} className="card-modern" data-testid={`global-stakeholder-${s.id}`}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-50 dark:bg-purple-900/40 flex items-center justify-center text-purple-600 dark:text-purple-400 font-bold text-sm">
                        {s.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-slate-800 dark:text-slate-100">{s.name}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {s.position || "No position"}{s.email ? ` · ${s.email}` : ""}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEditStakeholder(s)} data-testid={`edit-stakeholder-${s.id}`} className="dark:text-slate-300 dark:hover:bg-slate-700">
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/30" onClick={() => setDeleteTarget({ fn: () => handleDeleteStakeholder(s.id), label: s.name })} data-testid={`delete-stakeholder-${s.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {globalStakeholders.filter(s => s.type === "it").length === 0 && (
                <p className="text-sm text-slate-400 dark:text-slate-500 py-4 text-center">No IT stakeholders yet</p>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Teams Tab */}
        <TabsContent value="teams">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Input value={newTeamName} onChange={(e) => setNewTeamName(e.target.value)} placeholder="New team name..." className="max-w-xs dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100" data-testid="team-name-input"
                onKeyDown={async (e) => { if (e.key === "Enter" && newTeamName.trim()) { await axios.post(`${API_URL}/api/teams`, { name: newTeamName.trim() }); setNewTeamName(""); fetchData(); } }} />
              <Button onClick={async () => { if (newTeamName.trim()) { await axios.post(`${API_URL}/api/teams`, { name: newTeamName.trim() }); setNewTeamName(""); fetchData(); } }} size="sm" className="bg-[#0F172A] hover:bg-slate-800 text-white dark:bg-[#00D4C8] dark:hover:bg-[#00B8AE] dark:text-slate-900" data-testid="add-team-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Team
              </Button>
            </div>
            <div className="space-y-2">
              {teams.map((team) => (
                <div key={team.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700" data-testid={`team-row-${team.id}`}>
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{team.name}</span>
                  <button onClick={() => setDeleteTarget({ fn: async () => { await axios.delete(`${API_URL}/api/teams/${team.id}`); fetchData(); }, label: team.name })} className="p-1.5 rounded-md text-slate-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-all" data-testid={`team-delete-${team.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {teams.length === 0 && <p className="text-sm text-slate-400 dark:text-slate-500 py-4 text-center">No teams yet. Add your first team above.</p>}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* User Modal */}
      <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
        <DialogContent data-testid="user-modal" className="dark:bg-slate-800 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="dark:text-slate-100">{editingUser ? "Edit User" : "Create User"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Username</label>
              <Input value={userForm.username} onChange={(e) => setUserForm({ ...userForm, username: e.target.value })} required disabled={!!editingUser} data-testid="user-username" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">{editingUser ? "New Password (leave blank to keep)" : "Password"}</label>
              <Input type="password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} required={!editingUser} data-testid="user-password" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Full Name</label>
              <Input value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} required data-testid="user-name" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Email</label>
              <Input type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} data-testid="user-email" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Role</label>
              <Select value={userForm.role} onValueChange={(v) => setUserForm({ ...userForm, role: v })}>
                <SelectTrigger data-testid="user-role" className="dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="product_manager">Product Owner</SelectItem>
                  <SelectItem value="team_member_business">Team Member (Business)</SelectItem>
                  <SelectItem value="team_member_it">Team Member (IT)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowUserModal(false)} className="dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700">Cancel</Button>
              <Button type="submit" data-testid="save-user-btn">{editingUser ? "Update" : "Create"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Product Modal */}
      <Dialog open={showProductModal} onOpenChange={setShowProductModal}>
        <DialogContent data-testid="product-modal" className="dark:bg-slate-800 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="dark:text-slate-100">{editingProduct ? "Edit Product" : "Create Product"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateProduct} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Product Name</label>
              <Input value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} required data-testid="product-name" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Description</label>
              <Input value={productForm.description} onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} data-testid="product-description" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowProductModal(false)} className="dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700">Cancel</Button>
              <Button type="submit" data-testid="save-product-btn">{editingProduct ? "Update" : "Create"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Users Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className="max-w-lg dark:bg-slate-800 dark:border-slate-700" data-testid="assign-modal">
          <DialogHeader>
            <DialogTitle className="dark:text-slate-100">Assign Users to {selectedProductForAssign?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">Add User</label>
              <Select onValueChange={(userId) => handleAssignUser(userId)}>
                <SelectTrigger data-testid="assign-user-select" className="dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100">
                  <SelectValue placeholder="Select a user to add..." />
                </SelectTrigger>
                <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                  {users.filter(u => !assignedUsers.find(au => au.id === u.id)).map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name} (@{u.username})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">Assigned Users ({assignedUsers.length})</label>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {assignedUsers.map(u => (
                  <div key={u.id} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-700/50 rounded-lg" data-testid={`assigned-user-${u.id}`}>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-600 flex items-center justify-center text-sm font-medium dark:text-slate-200">
                        {u.name.charAt(0)}
                      </div>
                      <div>
                        <p className="text-sm font-medium dark:text-slate-100">{u.name}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{ROLE_LABELS[u.role]}</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" className="text-red-500 dark:text-red-400 dark:hover:bg-red-900/30" onClick={() => setDeleteTarget({ fn: () => handleRemoveUser(u.id), label: u.name || u.username })} data-testid={`remove-user-${u.id}`}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                {assignedUsers.length === 0 && (
                  <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-4">No users assigned yet</p>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* User Products Assignment Modal */}
      <Dialog open={showUserProductsModal} onOpenChange={setShowUserProductsModal}>
        <DialogContent className="max-w-lg dark:bg-slate-800 dark:border-slate-700" data-testid="user-products-modal">
          <DialogHeader>
            <DialogTitle className="dark:text-slate-100">Assign {selectedUserForProducts?.name} to Products</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500 dark:text-slate-400">Select the products this user should have access to:</p>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {products.map(p => {
                const isAssigned = userAssignedProducts.includes(p.id);
                return (
                  <div 
                    key={p.id} 
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${isAssigned ? 'bg-[#00D4C8]/10 border-[#00D4C8] dark:bg-[#00D4C8]/20' : 'bg-white dark:bg-slate-700/50 border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'}`}
                    onClick={() => toggleUserProduct(p.id)}
                    data-testid={`toggle-product-${p.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isAssigned ? 'bg-[#00D4C8]/20' : 'bg-slate-100 dark:bg-slate-600'}`}>
                        <Package className={`w-5 h-5 ${isAssigned ? 'text-[#00D4C8]' : 'text-slate-400 dark:text-slate-300'}`} />
                      </div>
                      <div>
                        <p className={`font-medium ${isAssigned ? 'text-[#00D4C8]' : 'text-slate-800 dark:text-slate-100'}`}>{p.name}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-[200px]">{p.description || 'No description'}</p>
                      </div>
                    </div>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${isAssigned ? 'bg-[#00D4C8] border-[#00D4C8]' : 'border-slate-300 dark:border-slate-500'}`}>
                      {isAssigned && (
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                );
              })}
              {products.length === 0 && (
                <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-4">No products created yet</p>
              )}
            </div>
            <div className="pt-2 border-t dark:border-slate-600">
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {userAssignedProducts.length} of {products.length} products assigned
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Stakeholder Modal */}
      <Dialog open={showStakeholderModal} onOpenChange={setShowStakeholderModal}>
        <DialogContent data-testid="stakeholder-modal" className="dark:bg-slate-800 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="dark:text-slate-100">{editingStakeholder ? "Edit Stakeholder" : "Add Stakeholder"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateStakeholder} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Full Name</label>
              <Input value={stakeholderForm.name} onChange={(e) => setStakeholderForm({ ...stakeholderForm, name: e.target.value })} required data-testid="stakeholder-name" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Position / Title</label>
              <Input value={stakeholderForm.position} onChange={(e) => setStakeholderForm({ ...stakeholderForm, position: e.target.value })} placeholder="e.g. Director, Manager" data-testid="stakeholder-position" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Email</label>
              <Input type="email" value={stakeholderForm.email} onChange={(e) => setStakeholderForm({ ...stakeholderForm, email: e.target.value })} placeholder="Optional" data-testid="stakeholder-email" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Type</label>
              <Select value={stakeholderForm.type} onValueChange={(v) => setStakeholderForm({ ...stakeholderForm, type: v })}>
                <SelectTrigger data-testid="stakeholder-type" className="dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                  <SelectItem value="business">Business</SelectItem>
                  <SelectItem value="it">IT</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowStakeholderModal(false)} className="dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700">Cancel</Button>
              <Button type="submit" data-testid="save-stakeholder-btn">{editingStakeholder ? "Update" : "Add"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
    <ConfirmDeleteDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={() => { deleteTarget?.fn(); }} title="Confirm Delete" description={`Are you sure you want to delete "${deleteTarget?.label}"? This action cannot be undone.`} />
    </>
  );
}
